import { restaurantProviders } from '../../enums/restaurantProviders';
import { restaurantTypes } from '../../enums/restaurantTypes';
import { formFileUrlFromFileArray } from '../../utils/fileHelper';

export const mapApiToDataRestaurant = ({ restaurant, company }) => {
  if (!restaurant) {
    return null;
  }

  global.localStorage.setItem('v2-company-id', company ? company._id : null);

  return {
    id: restaurant._id,
    name: restaurant.name,
    address: restaurant.address,
    street: restaurant.street,
    city: restaurant.city,
    country: restaurant.country,
    zip: restaurant.zip,
    companyId: company ? company._id : null,
    companyName: company ? company.name : null,
    timeZone: restaurant.zone,
    allowTipsInOrder: restaurant.allow_tips || restaurant.type === restaurantTypes.TipsOnly,
    allowTipsNoOrder: restaurant.allow_tips_alone || restaurant.type === restaurantTypes.TipsOnly,
    allowReviewsNoOrder: restaurant.allow_reviews_alone,
    forceDisableTips: restaurant.force_disable_tips,
    customHomeTitle: restaurant.client_home_title,
    restrictionsAge: restaurant.restrictions_age || 18, // 18 if not set
    reviewLink: restaurant.review_link,
    currency: restaurant.currency,
    location: restaurant.location,
    bannerUrl: formFileUrlFromFileArray(restaurant.banner),
    logoUrl: formFileUrlFromFileArray(restaurant.logo),
    eventsChannel: restaurant.events_channel,
    eventsGroup: restaurant.events_group,
    type: restaurant.type,
    allowTipsWaiterSelect: restaurant.can_select_waiter || false,
    mustSelectWaiterForTip: restaurant.must_select_waiter || false,
    dontRequireGuests: restaurant.dont_require_guests,
    canTakeOut: restaurant.can_take_out,
    provider: restaurant.rkeeper_code ? restaurantProviders.RKeeper : restaurantProviders.Iiko,
    integrationId: restaurant.integration_id,
    defaultLanguageCode: restaurant.default_language_code,
    availableAppLanguages: restaurant.available_app_languages
      ? restaurant.available_app_languages.split(',')
      : null,
    expandBannerType: restaurant.expand_banner_type,
    requireRoomNumber: restaurant.require_room_number,
    useMenuOptimizations: restaurant.use_menu_optimizations,
    disableComment: restaurant.dine_in_disable_comment,
    allowCash: restaurant.dine_in_allow_cash,
    cashPayAtBarText: restaurant.cash_pay_at_bar_text,
    requireConfirmConditions: restaurant.require_confirm_conditions,
    requireConfirmAlcohol: restaurant.require_confirm_alcohol,
    showCompanyDetails: restaurant.show_company_details_dinein,
    registeredCompanyAddress: restaurant.registered_company_address,
    employees: restaurant.employees
      ? restaurant.employees
          .filter((x) => !x.cant_receive_tips)
          .map((employee) => ({
            id: employee._id,
            firstName: employee.first_name,
            lastName: employee.last_name,
            image: formFileUrlFromFileArray(employee.image),
          }))
      : [],
  };
};

export const mapApiToDataPaymentOption = ({ options }) => {
  if (!options || options.length === 0) {
    return null;
  }

  options = options.filter((x) => !x.hidden_dinein);
  if (!options || options.length === 0) {
    return null;
  }

  const cardOption = selectCorrectCardProvider(options);
  const bankOption = options.find((option) => option.types && option.types.includes('Bank'));

  const additionalWalletOption = selectAdditionalProviderForWallets(options, cardOption);

  const mapped = {
    canPayBank: !!bankOption,
    canPayCard: !!cardOption,
    bankProvider: bankOption ? bankOption.provider : null,
    bankProviderId: bankOption ? bankOption._id : null,
    cardProvider: cardOption ? cardOption.provider : null,
    cardProviderId: cardOption ? cardOption._id : null,

    // Additional for google/apple pay
    additionalWalletProvider: additionalWalletOption ? additionalWalletOption.provider : null,
    additionalWalletProviderId: additionalWalletOption ? additionalWalletOption._id : null,

    stripePublicKey: cardOption ? cardOption.stripe_public_key : null,
    stripeAllowCards: cardOption ? cardOption.stripe_allow_cards || false : false,
    stripeAllowWallets: cardOption ? cardOption.stripe_allow_wallets || false : false,
    stripeIsConnected: cardOption ? cardOption.stripe_is_connected || false : false,
    stripeConnectedId: cardOption ? cardOption.stripe_connected_id : null,
    zenUseRedirect: cardOption ? cardOption.zen_use_redirect || false : false,
  };

  if (additionalWalletOption && additionalWalletOption.provider === 'Stripe') {
    mapped.stripePublicKey = additionalWalletOption.stripe_public_key;
    mapped.stripeAllowCards = false;
    mapped.stripeAllowWallets = true;
  }

  if (mapped.cardProvider === 'Stripe') {
    // show card possibility by default if nothing is selected
    if (!mapped.stripeAllowCards && !mapped.stripeAllowWallets) {
      mapped.stripeAllowCards = true;
    } else {
      mapped.canPayCard = mapped.stripeAllowCards;
    }
  }

  return mapped;
};

const selectCorrectCardProvider = (options) => {
  const allCardOptions = options.filter((option) => option.types && option.types.includes('Card'));
  if (!allCardOptions || allCardOptions.length === 0) {
    return null;
  }

  // priority providers, in case using different provider for wallets
  const providersPriority = ['Zen', 'Paynt'];

  let selectedOption;
  providersPriority.forEach((provider) => {
    if (selectedOption) {
      return;
    }

    selectedOption = allCardOptions.find((x) => x.provider === provider);
  });

  if (!selectedOption) {
    // eslint-disable-next-line prefer-destructuring
    selectedOption = allCardOptions[0];
  }

  return selectedOption;
};

const selectAdditionalProviderForWallets = (options, cardOption) => {
  const allCardOptions = options.filter((option) => option.types && option.types.includes('Card'));
  if (!allCardOptions || allCardOptions.length === 0) {
    return null;
  }

  const allowedProviders = ['Zen', 'Paynt'];
  if (!cardOption || !cardOption.provider || !allowedProviders.includes(cardOption.provider)) {
    return null;
  }

  const allowedWalletProvidersPriority = ['Stripe'];

  let selectedOption;
  allowedWalletProvidersPriority.forEach((provider) => {
    if (selectedOption) {
      return;
    }

    selectedOption = allCardOptions.find((x) => x.provider === provider);
    if (selectedOption && provider === 'Stripe' && !selectedOption.stripe_allow_wallets) {
      selectedOption = null;
    }
  });

  return selectedOption;
};
